import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Row, Col, Alert } from 'react-bootstrap';
import { importInvoiceItems } from '../http/invoiceAPI';
import { fetchStores, fetchCategories, fetchSizes, fetchProductByBarcode } from '../http/productAPI';

const typeOptions = [
  { value: 'shoe', label: 'Обувь' },
  { value: 'clothes', label: 'Одежда' },
  { value: 'toy', label: 'Игрушки' },
  { value: 'other', label: 'Прочее' }
];

// Базовый URL API без завершающего слэша, чтобы не было "//" в путях картинок
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const defaultRow = {
  id: Date.now() + Math.random(),
  barcode: '',
  name: '',
  quantity: 1,
  purchasePrice: '',
  type: 'other',
  store_id: '',
  category_id: '',
  size_id: '',
  color: '',
  gender: '', // Пол для обуви
  description: '',
  photo: null,
  // Привязка к существующему товару по штрих‑коду
  targetProductId: null,
  matchedProducts: [],
  variants: [] // Вложенные варианты с размерами и цветами
};

const InvoiceImportModal = ({ show, onHide, invoiceId, invoice, onImported }) => {
  const [rows, setRows] = useState([ { ...defaultRow } ]);
  const [defaultMarkup, setDefaultMarkup] = useState(65);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]); // Все размеры
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [productSelectModal, setProductSelectModal] = useState({ show: false, rowIdx: null, products: [] });
  const [searchingBarcode, setSearchingBarcode] = useState({});
  const [alreadyImported, setAlreadyImported] = useState(0);

  // При открытии модалки для конкретной накладной запоминаем,
  // сколько по ней уже было импортировано ранее
  useEffect(() => {
    const val = invoice ? Number(invoice.imported_amount || 0) : 0;
    setAlreadyImported(isNaN(val) ? 0 : val);
  }, [invoice]);

  // Когда закрыли модалку выбора товара, но основная ещё открыта –
  // убираем лишние bootstrap-модалки/бекдропы, оставляя только одну.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (show && !productSelectModal.show) {
      const modals = Array.from(document.querySelectorAll('.modal.show'));
      if (modals.length > 1) {
        // Оставляем первую (основную) модалку, остальные скрываем
        modals.slice(1).forEach(el => {
          el.classList.remove('show');
          el.classList.remove('fade');
          el.style.display = 'none';
        });
      }
      const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'));
      if (backdrops.length > 1) {
        backdrops.slice(1).forEach(el => el.remove());
      }
    }
  }, [show, productSelectModal.show]);

  // Жёсткая очистка только классов/стилей body, когда НАШИ окна закрыты.
  // ВАЖНО: не трогаем реальные DOM‑ноды .modal / .modal-backdrop,
  // чтобы не конфликтовать с внутренним порталом react‑bootstrap
  // (иначе React при unmount получает NotFoundError: removeChild).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    if (!show && !productSelectModal.show) {
      // Сбрасываем только класс и инлайновые стили, которые делают страницу некликабельной
      body.classList.remove('modal-open');
      body.style.removeProperty('overflow');
      body.style.removeProperty('paddingRight');
      body.style.removeProperty('padding-right');
    }
  }, [show, productSelectModal.show]);

  // Округление цены: последняя цифра 0 или 5, всегда вверх (должна совпадать с серверной логикой)
  const roundPriceTo05Up = (value) => {
    const num = Number(value) || 0;
    if (num <= 0) return 0;
    let rub = Math.ceil(num);
    const last = rub % 10;
    if (last === 0 || last === 5) return rub;
    if (last < 5) {
      return rub + (5 - last);
    }
    return rub + (10 - last);
  };

  const calcSellPrice = (purchase) => {
    const p = Number(purchase) || 0;
    const m = Number(defaultMarkup) || 0;
    const raw = p * (1 + m / 100);
    return roundPriceTo05Up(raw);
  };

  useEffect(() => {
    fetchStores().then(setStores).catch(()=>{});
    fetchCategories().then(setCategories).catch(()=>{});
    // Загружаем все размеры один раз
    Promise.all([
      fetchSizes('shoe').catch(()=>[]),
      fetchSizes('clothes').catch(()=>[])
    ]).then(([shoeSizes, clothesSizes]) => {
      setSizes([...shoeSizes, ...clothesSizes]);
    }).catch(()=>{});
  }, []);

  const categoryByType = useMemo(() => {
    return categories.reduce((acc, c) => {
      if (!acc[c.type]) acc[c.type] = [];
      acc[c.type].push(c);
      return acc;
    }, {});
  }, [categories]);

  // Фильтруем размеры по типу и категории для каждой строки
  const getSizesForRow = (rowType, categoryId) => {
    if (rowType !== 'shoe' && rowType !== 'clothes') return [];
    if (!sizes.length) return [];
    const category = categories.find(c => c.id === categoryId);
    if (!category) return sizes.filter(s => s.type === rowType);
    // Фильтруем по типу и категории (если категория указана в размере)
    return sizes.filter(s => {
      if (s.type !== rowType) return false;
      // Если у размера указана категория, она должна совпадать с выбранной категорией
      if (s.category) {
        return s.category === category.name || s.category === null;
      }
      return true; // Если категория не указана, показываем все размеры этого типа
    });
  };

  useEffect(() => {
    // Сброс неподходящих категорий и размеров при смене списка категорий
    setRows(prev => prev.map(r => {
      const available = categoryByType[r.type] || [];
      if (r.category_id && !available.some(c => c.id === r.category_id)) {
        return { ...r, category_id: '', size_id: '' };
      }
      // Сброс размера, если он не подходит для текущей категории
      if (r.category_id && r.size_id) {
        const availableSizes = getSizesForRow(r.type, r.category_id);
        if (!availableSizes.some(s => s.id === r.size_id)) {
          return { ...r, size_id: '' };
        }
      }
      return r;
    }));
  }, [categoryByType, sizes]);

  const addRow = () => setRows(prev => [...prev, { ...defaultRow, id: Date.now() + Math.random() }]);
  const removeRow = (idx) => setRows(prev => prev.filter((_,i)=>i!==idx));
  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r,i)=> {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      // При изменении штрих-кода запускаем поиск товаров
      if (field === 'barcode' && value && value.trim().length >= 3) {
        searchProductsByBarcode(idx, value.trim());
      } else if (field === 'barcode' && (!value || value.trim().length < 3)) {
        // Очищаем результаты поиска если штрих-код удалён
        updated.matchedProducts = [];
        updated.targetProductId = null;
      }
      return updated;
    }));
  };

  const searchProductsByBarcode = async (rowIdx, barcode) => {
    if (searchingBarcode[rowIdx]) return; // Уже ищем
    setSearchingBarcode(prev => ({ ...prev, [rowIdx]: true }));
    try {
      const result = await fetchProductByBarcode(barcode);
      if (result.multipleProducts && result.products && result.products.length > 0) {
        // Найдено несколько товаров - просто сохраняем список, окно откроется по клику
        setRows(prev => prev.map((r, i) => 
          i === rowIdx ? { ...r, matchedProducts: result.products } : r
        ));
      } else if (result.id) {
        // Найден один товар - автоматически привязываем
        setRows(prev => prev.map((r, i) => 
          i === rowIdx ? { ...r, targetProductId: result.id, matchedProducts: [result] } : r
        ));
      } else {
        // Товар не найден
        setRows(prev => prev.map((r, i) => 
          i === rowIdx ? { ...r, matchedProducts: [], targetProductId: null } : r
        ));
      }
    } catch (e) {
      // Товар не найден или ошибка - продолжаем без привязки
      setRows(prev => prev.map((r, i) => 
        i === rowIdx ? { ...r, matchedProducts: [], targetProductId: null } : r
      ));
    } finally {
      setSearchingBarcode(prev => {
        const next = { ...prev };
        delete next[rowIdx];
        return next;
      });
    }
  };

  const openProductSelectModal = (rowIdx) => {
    const row = rows[rowIdx];
    if (!row || !row.matchedProducts || row.matchedProducts.length === 0) return;
    setProductSelectModal({ show: true, rowIdx, products: row.matchedProducts });
  };

  const handleSelectProduct = (productId) => {
    const { rowIdx } = productSelectModal;
    setRows(prev => prev.map((r, i) => 
      i === rowIdx ? { ...r, targetProductId: productId } : r
    ));
    setProductSelectModal({ show: false, rowIdx: null, products: [] });
  };

  const handleCreateNewProduct = () => {
    const { rowIdx } = productSelectModal;
    setRows(prev => prev.map((r, i) => 
      i === rowIdx ? { ...r, targetProductId: null } : r
    ));
    setProductSelectModal({ show: false, rowIdx: null, products: [] });
  };

  const clearRowBinding = (rowIdx) => {
    setRows(prev => prev.map((r, i) => 
      i === rowIdx ? { ...r, targetProductId: null } : r
    ));
  };
  const updateRowAndBelow = (idx, field, value) => {
    setRows(prev => prev.map((r,i)=> {
      if (i < idx) return r;
      // При смене категории очищаем размер
      if (field === 'category_id') {
        return { ...r, [field]: value, size_id: '' };
      }
      return { ...r, [field]: value };
    }));
  };
  const updateTypeAndBelow = (idx, value) => setRows(prev => prev.map((r,i)=> i < idx ? r : { ...r, type: value, category_id: '', size_id: '' }));
  
  const addVariant = (rowIdx) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const base = { ...r };
      const variant = {
        id: Date.now() + Math.random(),
        barcode: '',
        name: base.name,
        quantity: 1,
        purchasePrice: base.purchasePrice,
        type: base.type,
        store_id: base.store_id,
        category_id: base.category_id,
        size_id: '',
        color: '',
        description: base.description,
        photo: null
      };
      return { ...base, variants: [...(base.variants || []), variant] };
    }));
  };
  
  const removeVariant = (rowIdx, variantIdx) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      return { ...r, variants: r.variants.filter((_, vi) => vi !== variantIdx) };
    }));
  };
  
  const updateVariant = (rowIdx, variantIdx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const updatedVariants = [...(r.variants || [])];
      updatedVariants[variantIdx] = { ...updatedVariants[variantIdx], [field]: value };
      return { ...r, variants: updatedVariants };
    }));
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    const lines = text.split('\n').map(l=>l.trim()).filter(Boolean);
    if (!lines.length) return;
    const parsed = lines.map(line => {
      const parts = line.split(/\t|;/).map(p=>p.trim());
      const rawQty = parts[2] || '1';
      const rawPurchase = parts[3] || '';
      const qty = Number(rawQty.replace(',', '.')) || 1;
      const purchase = rawPurchase.replace(/\s/g, '').replace(',', '.');
      return {
        ...defaultRow,
        id: Date.now() + Math.random(),
        barcode: parts[0] || '',
        name: parts[1] || '',
        quantity: qty,
        purchasePrice: purchase,
        type: parts[4] || 'other',
        description: parts[5] || '',
        variants: []
      };
    });
    setRows(parsed);

    // Через небольшой таймаут запускаем проверку штрих‑кодов
    // для всех вставленных строк, чтобы НЕ нужно было кликать в ячейку.
    setTimeout(() => {
      parsed.forEach((row, idx) => {
        const barcode = (row.barcode || '').trim();
        if (barcode.length >= 3) {
          searchProductsByBarcode(idx, barcode);
        }
      });
    }, 50);
  };

  const validRows = useMemo(() => rows.filter(r => r.name && Number(r.purchasePrice) > 0 && Number(r.quantity) > 0 && r.store_id && r.category_id), [rows]);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalPurchase = 0;
    let totalSell = 0;

    rows.forEach((r) => {
      const qty = Number(r.quantity) || 0;
      const purchase = Number(r.purchasePrice) || 0;
      const type = r.type || 'other';
      const hasSizeOrVariants =
        (type === 'shoe' || type === 'clothes') &&
        ((r.size_id || r.color) || (r.variants && r.variants.length > 0));

      // Для обуви/одежды, если есть размеры/варианты – основную строку считаем только когда она без размеров
      if (qty > 0 && purchase > 0 && (!(type === 'shoe' || type === 'clothes') || !hasSizeOrVariants)) {
        const sell = calcSellPrice(purchase);
        totalQty += qty;
        totalPurchase += qty * purchase;
        totalSell += qty * sell;
      }

      // Варианты
      (r.variants || []).forEach((v) => {
        const vQty = Number(v.quantity) || 0;
        const vPurchase = Number(v.purchasePrice || r.purchasePrice) || 0;
        const vType = v.type || type;
        if (!vQty || !vPurchase) return;
        if ((vType === 'shoe' || vType === 'clothes') && !v.size_id) return;
        const sell = calcSellPrice(vPurchase);
        totalQty += vQty;
        totalPurchase += vQty * vPurchase;
        totalSell += vQty * sell;
      });
    });

    return {
      totalQty,
      totalPurchase,
      totalSell,
    };
  }, [rows, defaultMarkup]);

  const formatMoney = (value) =>
    (Number(value) || 0).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const combinedImported = useMemo(
    () => (alreadyImported || 0) + (totals.totalPurchase || 0),
    [alreadyImported, totals.totalPurchase]
  );

  const handleImport = async () => {
    if (!invoiceId) { setError('Не выбрана накладная'); return; }
    if (!validRows.length) { setError('Заполните товары: имя, сумма, количество, магазин и категорию'); return; }
    setError('');
    setLoading(true);
    try {
      // Формируем плоский список: основная строка + все её варианты
      const flatItems = [];
      rows.forEach((r, rowIdx) => {
        // Основная строка - создаётся как товар БЕЗ размера (даже если указан размер и цвет)
        // Размер и цвет из основной строки будут добавлены как первый вариант
        flatItems.push({
          barcode: r.barcode,
          name: r.name,
          purchasePrice: Number(r.purchasePrice)||0,
          quantity: Number(r.quantity)||0,
          type: r.type,
          store_id: r.store_id,
          category_id: r.category_id || null,
          gender: r.type === 'shoe' ? (r.gender || null) : null,
          size_id: null, // Основной товар БЕЗ размера
          color: null, // Основной товар БЕЗ цвета
          description: r.description || '',
          isMain: true,
          rowIdx,
          photoIdx: rowIdx,
          // Если у основной строки указан размер и цвет, они будут созданы как вариант
          mainSizeId: (r.type === 'shoe' || r.type === 'clothes') ? (r.size_id || null) : null,
          mainColor: (r.type === 'shoe' || r.type === 'clothes') ? (r.color || null) : null,
          // Привязка к существующему товару
          existingProductId: r.targetProductId || null
        });
        // Варианты этой строки (включая размер+цвет из основной строки, если они указаны)
        // Сначала добавляем вариант из основной строки, если есть размер
        if ((r.type === 'shoe' || r.type === 'clothes') && (r.size_id || r.color)) {
          flatItems.push({
            barcode: r.barcode || '',
            name: r.name,
            purchasePrice: Number(r.purchasePrice)||0,
            quantity: Number(r.quantity)||0,
            type: r.type,
            store_id: r.store_id,
            category_id: r.category_id || null,
            gender: r.type === 'shoe' ? (r.gender || null) : null,
            size_id: r.size_id || null,
            color: r.color || 'Не указан',
            description: r.description || '',
            isVariant: true,
            parentRowIdx: rowIdx,
            variantIdx: -1 // -1 означает, что это вариант из основной строки
          });
        }
        // Теперь добавляем все остальные варианты
        (r.variants || []).forEach((v, vIdx) => {
          flatItems.push({
            barcode: v.barcode,
            name: v.name || r.name,
            purchasePrice: Number(v.purchasePrice)||0,
            quantity: Number(v.quantity)||0,
            type: v.type || r.type,
            store_id: v.store_id || r.store_id,
            category_id: v.category_id || r.category_id || null,
            gender: (v.type || r.type) === 'shoe' ? (v.gender || r.gender || null) : null,
            size_id: v.size_id || null,
            color: v.color || ((v.type || r.type) === 'shoe' || (v.type || r.type) === 'clothes' ? 'Не указан' : null),
            description: v.description || r.description || '',
            isVariant: true,
            parentRowIdx: rowIdx,
            variantIdx: vIdx
          });
        });
      });
      
      // Логирование для отладки
      console.log('[InvoiceImportModal] Отправка данных:', {
        totalItems: flatItems.length,
        items: flatItems.map(item => ({
          name: item.name,
          type: item.type,
          isMain: item.isMain,
          isVariant: item.isVariant,
          parentRowIdx: item.parentRowIdx,
          rowIdx: item.rowIdx,
          quantity: item.quantity,
          store_id: item.store_id
        }))
      });
      
      const form = new FormData();
      form.append('invoiceId', invoiceId);
      form.append('defaultMarkup', defaultMarkup);
      form.append('items', JSON.stringify(flatItems));
      
      // Добавляем фото основной строки и вариантов
      let photoCount = 0;
      rows.forEach((r, idx) => {
        // Фото основной строки
        if (r.photo) {
          form.append(`photo_${idx}`, r.photo);
          photoCount++;
          console.log(`[InvoiceImportModal] Добавлено фото основной строки ${idx}:`, r.photo.name);
        }
        // Фото вариантов
        if (r.variants && r.variants.length > 0) {
          r.variants.forEach((v, vIdx) => {
            if (v.photo) {
              form.append(`photo_${idx}_variant_${vIdx}`, v.photo);
              photoCount++;
              console.log(`[InvoiceImportModal] Добавлено фото варианта ${idx}-${vIdx}:`, v.photo.name);
            }
          });
        }
      });
      console.log(`[InvoiceImportModal] Всего добавлено фотографий: ${photoCount}`);
      
      await importInvoiceItems(form);
      if (onImported) onImported();
      onHide();
      setRows([ { ...defaultRow, id: Date.now() + Math.random() } ]);
    } catch (e) {
      setError(e?.response?.data?.message || 'Ошибка при импорте товаров');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Добавить товары из накладной</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="mb-3 g-2">
          <Col md={3} sm={6} xs={12}>
            <Form.Label>Наценка по умолчанию (%)</Form.Label>
            <Form.Control type="number" value={defaultMarkup} onChange={e=>setDefaultMarkup(Number(e.target.value)||0)} />
          </Col>
          <Col md={9} sm={6} xs={12}>
            <Form.Label>Быстрый ввод (вставка строк: Штрих-код \t Название \t Кол-во \t Закупка \t Тип \t Описание)</Form.Label>
            <Form.Control as="textarea" rows={2} placeholder="Вставьте строки из Excel/таблички" onPaste={handlePaste} />
          </Col>
        </Row>
        <div className="table-responsive" style={{ maxHeight: 360, overflow: 'auto' }}>
          <Table bordered hover size="sm" className="align-middle" style={{ tableLayout: 'fixed', width: '100%', fontSize: '0.875rem' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
              <tr>
                <th style={{width:'110px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'110px'}}>Штрих-код</th>
                <th style={{width:'180px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'180px'}}>Название</th>
                <th style={{width:'75px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'75px'}}>Кол-во</th>
                <th style={{width:'100px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'100px'}}>Закупка</th>
                <th style={{width:'110px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'110px'}}>Тип</th>
                <th style={{width:'110px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'110px'}}>Пол</th>
                <th style={{width:'140px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'140px'}}>Категория</th>
                <th style={{width:'100px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'100px'}}>Размер</th>
                <th style={{width:'90px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'90px'}}>Цвет</th>
                <th style={{width:'150px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'150px'}}>Описание</th>
                <th style={{width:'180px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'180px'}}>Фото</th>
                <th style={{width:'140px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'140px'}}>Магазин</th>
                <th style={{width:'80px', whiteSpace:'nowrap', padding:'8px 4px', overflow:'hidden', maxWidth:'80px'}}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <React.Fragment key={r.id || idx}>
                  {/* Основная строка */}
                  <tr style={{ 
                    backgroundColor: r.targetProductId ? '#fff3cd' : '#f8f9fa',
                    borderLeft: r.targetProductId ? '4px solid #ffc107' : 'none'
                  }}>
                    <td style={{width:'110px', padding:'4px', overflow:'hidden', maxWidth:'110px', position:'relative'}}>
                      <Form.Control 
                        size="sm"
                        value={r.barcode} 
                        onChange={e=>updateRow(idx,'barcode',e.target.value)}
                        onBlur={() => {
                          // При потере фокуса запускаем поиск если штрих-код достаточно длинный
                          if (r.barcode && r.barcode.trim().length >= 3 && !r.targetProductId) {
                            searchProductsByBarcode(idx, r.barcode.trim());
                          }
                        }}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      />
                      {searchingBarcode[idx] && (
                        <div className="position-absolute" style={{right:'4px', top:'50%', transform:'translateY(-50%)', fontSize:'10px'}}>
                          <span className="spinner-border spinner-border-sm" role="status" />
                        </div>
                      )}
                      {r.targetProductId && (
                        <div className="small mt-1 d-flex flex-column">
                          <span className="text-success" title="Товар будет добавлен в существующую карточку">
                            ✓ Привязан к товару
                          </span>
                          <Button
                            variant="link"
                            size="sm"
                            type="button"
                            className="p-0 text-decoration-none text-danger"
                            onClick={() => clearRowBinding(idx)}
                          >
                            Снять привязку
                          </Button>
                        </div>
                      )}
                      {r.matchedProducts && r.matchedProducts.length > 0 && !r.targetProductId && (
                        <div
                          className="mt-1"
                          role="button"
                          style={{ cursor: 'pointer' }}
                          title="Найдены товары с таким штрих-кодом. Нажмите, чтобы выбрать."
                          onClick={() => openProductSelectModal(idx)}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            type="button"
                            className="p-0 small text-warning text-decoration-none"
                          >
                            ⚠ Найдено {r.matchedProducts.length} товар(ов)
                          </Button>
                        </div>
                      )}
                    </td>
                    <td style={{width:'180px', padding:'4px', overflow:'hidden', maxWidth:'180px'}}>
                      <Form.Control 
                        size="sm"
                        value={r.name} 
                        onChange={e=>updateRow(idx,'name',e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      />
                    </td>
                    <td style={{width:'75px', padding:'4px', overflow:'hidden', maxWidth:'75px'}}>
                      <Form.Control 
                        size="sm"
                        type="number" 
                        min="0" 
                        value={r.quantity} 
                        onChange={e=>updateRow(idx,'quantity',e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      />
                    </td>
                    <td style={{width:'100px', padding:'4px', overflow:'hidden', maxWidth:'100px'}}>
                      <Form.Control 
                        size="sm"
                        type="number" 
                        min="0" 
                        step="0.01" 
                        value={r.purchasePrice} 
                        onChange={e=>updateRow(idx,'purchasePrice',e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      />
                    </td>
                    <td style={{width:'110px', padding:'4px', overflow:'hidden', maxWidth:'110px'}}>
                      <Form.Select 
                        size="sm"
                        value={r.type} 
                        onChange={e=>updateTypeAndBelow(idx,e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      >
                        {typeOptions.map(opt=>(<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </Form.Select>
                    </td>
                    <td style={{width:'110px', padding:'4px', overflow:'hidden', maxWidth:'110px'}}>
                      {r.type === 'shoe' ? (
                        <Form.Select
                          size="sm"
                          value={r.gender || ''}
                          onChange={e=>updateRowAndBelow(idx,'gender',e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        >
                          <option value="">Без пола</option>
                          <option value="мужской">Мужской</option>
                          <option value="женский">Женский</option>
                        </Form.Select>
                      ) : (
                        <div className="text-muted small text-center">-</div>
                      )}
                    </td>
                    <td style={{width:'140px', padding:'4px', overflow:'hidden', maxWidth:'140px'}}>
                      <Form.Select
                        size="sm"
                        value={r.category_id||''}
                        onChange={e=>updateRowAndBelow(idx,'category_id',e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      >
                        <option value="">Без категории</option>
                        {(categoryByType[r.type] || []).map(c=>(<option key={c.id} value={c.id}>{c.name}</option>))}
                      </Form.Select>
                    </td>
                    <td style={{width:'100px', padding:'4px', overflow:'hidden', maxWidth:'100px'}}>
                      {(r.type === 'shoe' || r.type === 'clothes') ? (
                        <Form.Select
                          size="sm"
                          value={r.size_id || ''}
                          onChange={e=>updateRow(idx, 'size_id', e.target.value)}
                          disabled={!r.category_id}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        >
                          <option value="">Размер</option>
                          {getSizesForRow(r.type, r.category_id).map(s => (
                            <option key={s.id} value={s.id}>{s.value}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div className="text-muted small text-center">-</div>
                      )}
                    </td>
                    <td style={{width:'90px', padding:'4px', overflow:'hidden', maxWidth:'90px'}}>
                      {(r.type === 'shoe' || r.type === 'clothes') ? (
                        <Form.Control
                          size="sm"
                          type="text"
                          value={r.color || ''}
                          onChange={e=>updateRow(idx, 'color', e.target.value)}
                          placeholder="Цвет"
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        />
                      ) : (
                        <div className="text-muted small text-center">-</div>
                      )}
                    </td>
                    <td style={{width:'150px', padding:'4px', overflow:'hidden', maxWidth:'150px'}}>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        rows={1}
                        value={r.description}
                        placeholder="Описание"
                        onChange={e=>updateRow(idx,'description',e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem', resize:'none'}}
                      />
                    </td>
                    <td style={{width:'180px', padding:'4px', overflow:'hidden', maxWidth:'180px'}}>
                      <Form.Control
                        size="sm"
                        type="file"
                        accept="image/*"
                        onChange={e=>updateRow(idx,'photo', e.target.files?.[0] || null)}
                        onPaste={async e => {
                          const item = Array.from(e.clipboardData?.items || []).find(it => it.type.startsWith('image/'));
                          if (item) {
                            e.preventDefault();
                            const blob = item.getAsFile();
                            if (blob) {
                              const file = new File([blob], `pasted-${Date.now()}.png`, { type: blob.type });
                              updateRow(idx, 'photo', file);
                            }
                          }
                        }}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      />
                      {r.photo && <div className="small text-muted mt-1 text-truncate" style={{maxWidth:'170px'}} title={r.photo.name}>{r.photo.name}</div>}
                    </td>
                    <td style={{width:'140px', padding:'4px', overflow:'hidden', maxWidth:'140px'}}>
                      <Form.Select 
                        size="sm"
                        value={r.store_id} 
                        onChange={e=>updateRowAndBelow(idx,'store_id',e.target.value)}
                        style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                      >
                        <option value="">Магазин</option>
                        {stores.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
                      </Form.Select>
                    </td>
                    <td style={{width:'80px', padding:'4px'}}>
                      <div className="d-flex gap-1 justify-content-center">
                        <Button
                          variant="success"
                          size="sm"
                          title="Добавить вариант"
                          onClick={() => addVariant(idx)}
                          style={{padding:'2px 6px', fontSize:'12px'}}
                        >
                          +
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Удалить строку"
                          onClick={()=>removeRow(idx)}
                          style={{padding:'2px 6px', fontSize:'12px'}}
                        >
                          ×
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {/* Вложенные варианты */}
                  {(r.variants || []).map((v, vIdx) => (
                    <tr key={v.id || `v-${idx}-${vIdx}`} style={{ backgroundColor: '#fff', borderLeft: '4px solid #28a745' }}>
                      <td style={{width:'110px', padding:'4px', paddingLeft: '1.5rem', overflow:'hidden', maxWidth:'110px'}}>
                        <Form.Control 
                          size="sm"
                          value={v.barcode || ''} 
                          onChange={e=>updateVariant(idx, vIdx, 'barcode', e.target.value)}
                          placeholder="Штрих-код"
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        />
                      </td>
                      <td style={{width:'180px', padding:'4px', overflow:'hidden', maxWidth:'180px'}}>
                        <Form.Control 
                          size="sm"
                          value={v.name || ''} 
                          onChange={e=>updateVariant(idx, vIdx, 'name', e.target.value)}
                          placeholder={r.name || 'Название'}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        />
                      </td>
                      <td style={{width:'75px', padding:'4px', overflow:'hidden', maxWidth:'75px'}}>
                        <Form.Control 
                          size="sm"
                          type="number" 
                          min="0" 
                          value={v.quantity || ''} 
                          onChange={e=>updateVariant(idx, vIdx, 'quantity', e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        />
                      </td>
                      <td style={{width:'100px', padding:'4px', overflow:'hidden', maxWidth:'100px'}}>
                        <Form.Control 
                          size="sm"
                          type="number" 
                          min="0" 
                          step="0.01" 
                          value={v.purchasePrice || ''} 
                          onChange={e=>updateVariant(idx, vIdx, 'purchasePrice', e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        />
                      </td>
                      <td style={{width:'110px', padding:'4px', overflow:'hidden', maxWidth:'110px'}}>
                        <Form.Select 
                          size="sm"
                          value={v.type || r.type} 
                          onChange={e=>updateVariant(idx, vIdx, 'type', e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        >
                          {typeOptions.map(opt=>(<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                        </Form.Select>
                      </td>
                      <td style={{width:'110px', padding:'4px', overflow:'hidden', maxWidth:'110px'}}>
                        {(v.type || r.type) === 'shoe' ? (
                          <Form.Select
                            size="sm"
                            value={v.gender || r.gender || ''}
                            onChange={e=>updateVariant(idx, vIdx, 'gender', e.target.value)}
                            style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                          >
                            <option value="">Без пола</option>
                            <option value="мужской">Мужской</option>
                            <option value="женский">Женский</option>
                            <option value="детский">Детский</option>
                          </Form.Select>
                        ) : (
                          <div className="text-muted small text-center">-</div>
                        )}
                      </td>
                      <td style={{width:'140px', padding:'4px', overflow:'hidden', maxWidth:'140px'}}>
                        <Form.Select
                          size="sm"
                          value={v.category_id || r.category_id || ''}
                          onChange={e=>updateVariant(idx, vIdx, 'category_id', e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        >
                          <option value="">Без категории</option>
                          {(categoryByType[v.type || r.type] || []).map(c=>(<option key={c.id} value={c.id}>{c.name}</option>))}
                        </Form.Select>
                      </td>
                      <td style={{width:'100px', padding:'4px', overflow:'hidden', maxWidth:'100px'}}>
                        {((v.type || r.type) === 'shoe' || (v.type || r.type) === 'clothes') ? (
                          <Form.Select
                            size="sm"
                            value={v.size_id || ''}
                            onChange={e=>updateVariant(idx, vIdx, 'size_id', e.target.value)}
                            disabled={!(v.category_id || r.category_id)}
                            style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                          >
                            <option value="">Размер</option>
                            {getSizesForRow(v.type || r.type, v.category_id || r.category_id).map(s => (
                              <option key={s.id} value={s.id}>{s.value}</option>
                            ))}
                          </Form.Select>
                        ) : (
                          <div className="text-muted small text-center">-</div>
                        )}
                      </td>
                      <td style={{width:'90px', padding:'4px', overflow:'hidden', maxWidth:'90px'}}>
                        {((v.type || r.type) === 'shoe' || (v.type || r.type) === 'clothes') ? (
                          <Form.Control
                            size="sm"
                            type="text"
                            value={v.color || ''}
                            onChange={e=>updateVariant(idx, vIdx, 'color', e.target.value)}
                            placeholder="Цвет"
                            style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                          />
                        ) : (
                          <div className="text-muted small text-center">-</div>
                        )}
                      </td>
                      <td style={{width:'150px', padding:'4px', overflow:'hidden', maxWidth:'150px'}}>
                        <Form.Control
                          size="sm"
                          as="textarea"
                          rows={1}
                          value={v.description || ''}
                          placeholder="Описание"
                          onChange={e=>updateVariant(idx, vIdx, 'description', e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem', resize:'none'}}
                        />
                      </td>
                      <td style={{width:'180px', padding:'4px', overflow:'hidden', maxWidth:'180px'}}>
                        <Form.Control
                          size="sm"
                          type="file"
                          accept="image/*"
                          onChange={e=>updateVariant(idx, vIdx, 'photo', e.target.files?.[0] || null)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        />
                        {v.photo && <div className="small text-muted mt-1 text-truncate" style={{maxWidth:'170px'}} title={v.photo.name}>{v.photo.name}</div>}
                      </td>
                      <td style={{width:'140px', padding:'4px', overflow:'hidden', maxWidth:'140px'}}>
                        <Form.Select 
                          size="sm"
                          value={v.store_id || r.store_id || ''} 
                          onChange={e=>updateVariant(idx, vIdx, 'store_id', e.target.value)}
                          style={{width:'100%', maxWidth:'100%', fontSize:'0.875rem'}}
                        >
                          <option value="">Магазин</option>
                          {stores.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
                        </Form.Select>
                      </td>
                      <td style={{width:'80px', padding:'4px'}}>
                        <div className="d-flex justify-content-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Удалить вариант"
                            onClick={() => removeVariant(idx, vIdx)}
                            style={{padding:'2px 6px', fontSize:'12px'}}
                          >
                            ×
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="d-flex justify-content-between mt-3">
          <Button variant="outline-secondary" onClick={addRow}>Добавить строку</Button>
          <div className="d-flex flex-column align-items-end text-muted small">
            <div>Готово строк: {validRows.length} / {rows.length}</div>
            <div>
              Итого текущий импорт:&nbsp;
              <strong>{totals.totalQty}</strong>&nbsp;шт,&nbsp;
              закупка&nbsp;
              <strong>{formatMoney(totals.totalPurchase)} ₽</strong>,&nbsp;
              продажа (с наценкой и округлением)&nbsp;
              <strong>{formatMoney(totals.totalSell)} ₽</strong>
            </div>
            {invoice && (
              <div className="mt-1">
                Уже было добавлено по этой накладной:&nbsp;
                <strong>{formatMoney(alreadyImported)} ₽</strong>
                {totals.totalPurchase > 0 && (
                  <>
                    , после этого импорта будет всего:&nbsp;
                    <strong>{formatMoney(combinedImported)} ₽</strong>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Отмена</Button>
        <Button variant="primary" onClick={handleImport} disabled={loading}>Импортировать товары</Button>
      </Modal.Footer>
    </Modal>

    {/* Модалка выбора существующего товара без собственного backdrop,
        чтобы не было второго затемняющего слоя и залипания fade/modal. */}
    <Modal
      show={productSelectModal.show}
      onHide={() => setProductSelectModal({ show: false, rowIdx: null, products: [] })}
      size="lg"
      centered
      backdrop={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Найдены товары с таким штрих-кодом</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          Выберите товар, в который нужно добавить количество, или создайте новую карточку товара.
        </Alert>
        <div className="list-group mb-3">
          {productSelectModal.products.map((product) => (
            <button
              key={product.id}
              type="button"
              className="list-group-item list-group-item-action"
              onClick={() => handleSelectProduct(product.id)}
            >
              <div className="d-flex w-100 justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="mb-1">{product.name}</h6>
                  <p className="mb-1 small text-muted">
                    Тип: {typeOptions.find(o => o.value === product.type)?.label || product.type}
                    {product.category && ` • Категория: ${product.category}`}
                  </p>
                  <p className="mb-0 small">
                    Цена: {Number(product.price || 0).toLocaleString('ru-RU')} ₽
                    {product.price_with_discount && product.price_with_discount !== product.price && (
                      <span className="text-success ms-2">
                        Со скидкой: {Number(product.price_with_discount).toLocaleString('ru-RU')} ₽
                      </span>
                    )}
                  </p>
                  {product.stock !== undefined && (
                    <p className="mb-0 small text-muted">Остаток: {product.stock} шт</p>
                  )}
                </div>
                {product.image_url && (
                  <img
                    src={`${API_BASE}/static/${product.image_url}`}
                    alt={product.name}
                    style={{ width: '60px', height: '60px', objectFit: 'cover', marginLeft: '10px' }}
                  />
                )}
              </div>
            </button>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => setProductSelectModal({ show: false, rowIdx: null, products: [] })}
        >
          Отмена
        </Button>
        <Button variant="primary" onClick={handleCreateNewProduct}>
          Создать новую карточку товара
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default InvoiceImportModal;




